import { useOrderContext } from "app/providers/OrderProvider";
import { usePaymentContext } from "pages/payment/providers/PaymentProvider";
import { useEffect } from "react";
import Card from "shared/ui/card";
import { concatClassnames } from "shared/utils/concatClassnames";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import { PaymentActions } from "shared/utils/payment-methods";
import StandardOrderPriceTable from "../../entities/standard-order-price-table";
import StandardProductsTotal from "../../entities/standard-products-total";
import { StandardOrderProduct } from "../../shared/api/middleware-api-client";
import SidebarStandardProductsList from "../shared/sidebar-standard-products-list";

interface PaymentSidebarStandardProductsProps {
  className?: string;
  products: StandardOrderProduct[];
}

const PaymentSidebarStandardProducts = ({
  className = "",
  products,
}: PaymentSidebarStandardProductsProps) => {
  const orderContext = useOrderContext();
  const {
    order,
    getOrderSubtotal,
    getWarrantyPackageTotal,
    getRawOrderDiscountRecordsPrice,
    setRemaining,
    getOrderTotalRemaining,
    getOrderTotalPaid,
    getWarrantyPackageDiscount,
    warrantyPackage,
    setWarrantyDiscount,
    getWarrantyOwnRiskTotal,
    setTotal,
    getVoucherDiscount,
  } = useOrderContext();
  const {
    paymentAction,
    setPaymentMethod,
    customAmount,
    paymentMethod,
    standardProductOrder,
  } = usePaymentContext();

  useEffect(() => {
    let method = paymentAction || "PAY_LATER";
    setPaymentMethod(method);

    if (method === "PAY_NOW") {
      let price = getOrderSubtotal();
      price += getWarrantyPackageTotal();
      price -= getRawOrderDiscountRecordsPrice();
      price = price * 0.1;
      setRemaining(getFormattedPrice(price));
    }

    if (method === "PAY_FULL") {
      let price = getOrderTotalRemaining();

      if (getOrderTotalPaid() === 0) {
        setWarrantyDiscount(getWarrantyPackageDiscount(method));
        price -= getWarrantyPackageDiscount(method);
      } else {
        setWarrantyDiscount(0);
      }

      setRemaining(getFormattedPrice(price));
    } else {
      setWarrantyDiscount(0);
    }

    if (
      method === "PAY_LATER" ||
      method === "PAY_LATER_MONTHS" ||
      method === "PAY_LATER_TERMS"
    ) {
      setRemaining("€0,-");
    }

    if (method === "PAY_CUSTOM" && customAmount) {
      setRemaining(getFormattedPrice(customAmount));
    }
  }, [paymentAction, warrantyPackage]);

  useEffect(() => {
    let discountPrice = getRawOrderDiscountRecordsPrice();
    let warrantyPrice = getWarrantyPackageTotal();
    let ownRiskPrice = getWarrantyOwnRiskTotal();
    const voucherDiscount = getVoucherDiscount();

    let totalPrice =
      orderContext.getOrderSubtotal() +
      warrantyPrice +
      ownRiskPrice -
      discountPrice -
      voucherDiscount -
      orderContext.getWarrantyPackageDiscount(paymentMethod);

    setTotal(totalPrice);
  }, [order, warrantyPackage, getVoucherDiscount]);

  useEffect(() => {
    if (paymentAction === PaymentActions.PAY_CUSTOM && customAmount) {
      setRemaining(getFormattedPrice(customAmount));
    }
  }, [customAmount]);

  // Return early if no data available
  if (!standardProductOrder || !products) return null;

  return (
    <section className={concatClassnames("gg-grid", className)}>
      <Card shadow={false} className="!gg-bg-grey-500">
        <div className="gg-px-9 gg-pt-9">
          <SidebarStandardProductsList products={products} />
        </div>

        <div className="gg-relative gg-grid gg-grid-cols-12 gg-gap-y-5 gg-px-9 gg-py-7">
          <hr className="gg-col-span-12 gg-mb-2" />
          <StandardOrderPriceTable
            entity={standardProductOrder}
            className="gg-col-span-12"
          />

          <hr className="gg-col-span-12 gg-mt-3" />

          <StandardProductsTotal
            entity={standardProductOrder}
            className="gg-col-span-12 gg-justify-self-end"
          />
        </div>
      </Card>
    </section>
  );
};
export default PaymentSidebarStandardProducts;
